// Display names for raw dimension values (DEV-2432 §2.3).
//
// The aggregation engine returns the warehouse's own values — ISO country codes,
// `GADS` / `FADS` / `BADS`, `PERFORMANCE_MAX`, lower-case `consumer` — and every
// chart surface (bars, legend, x-axis, tooltip) must show the user-friendly name
// instead. One function, keyed by dimension id + raw value, so the live page,
// the drawer previews and the tokenised PDF render route all agree.
//
// Rules, per the ticket:
//   • Country / Region / City — the full country name for a code; regions and
//     cities already carry their own name.
//   • Ad platform — Google Ads / Meta Ads / Microsoft Ads, never the code.
//   • Campaign type — underscores to spaces, first letter of each word up. The
//     rule is generic: it applies to every value any platform returns.
//   • IP usage / Device type — first letter of each word up.
//   • Operating system — canonical casing (iOS, macOS) passes through untouched.
//   • Status — the click veracity codes read as Valid / Suspicious / Bad.
//   • Everything else passes through as-is.

import { countryName } from '@clickguard/design-system'
import { ClickStatusMapper } from 'utils/click'
import type { DimensionId } from '../types'

// The ticket's wording ("Meta Ads"), which differs from the design system's
// `integrationLabel` ("Meta"); the brand-mark lookup stays in source-icon.
export const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
	GADS: 'Google Ads',
	FADS: 'Meta Ads',
	BADS: 'Microsoft Ads'
}

/** The empty-value placeholder the engine prints for a missing category. */
export const EMPTY_LABEL = '—'

/** `corporate` → `Corporate`, `data center` → `Data Center`. Only the first
 *  letter of each word is touched, so `iOS` would stay `iOS`. */
export const capitalizeWords = (raw: string): string =>
	raw
		.trim()
		.split(/\s+/)
		.map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
		.join(' ')

/** `PERFORMANCE_MAX` → `Performance Max`, `link_clicks` → `Link Clicks`. */
export const titleCaseWords = (raw: string): string =>
	capitalizeWords(raw.replace(/_/g, ' ').toLowerCase())

const statusLabel = (raw: string): string =>
	ClickStatusMapper[raw.toUpperCase() as keyof typeof ClickStatusMapper]
		?.label ?? capitalizeWords(raw.toLowerCase())

/**
 * The display name for one raw dimension value. `label` is the engine's own
 * label for the row (it already prints `—` for an empty value); an empty or
 * placeholder value is returned untouched so a missing category still LOOKS
 * missing.
 */
export const displayName = (
	dimensionId: DimensionId | string | undefined,
	rawValue: string,
	label?: string
): string => {
	const raw = rawValue ?? ''
	const fallback = label ?? raw
	if (!raw || raw === EMPTY_LABEL) return fallback || EMPTY_LABEL
	switch (dimensionId) {
		case 'country':
			return /^[A-Za-z]{2}$/.test(raw) ? countryName(raw) : raw
		case 'ad_platform':
			return PLATFORM_DISPLAY_NAMES[raw.toUpperCase()] ?? raw
		case 'campaign_type':
			return titleCaseWords(raw)
		case 'ip_usage':
		case 'device_type':
			return capitalizeWords(raw)
		case 'status':
			return statusLabel(raw)
		default:
			return fallback
	}
}
